import { useRef, useState } from 'react';
import { useCountDown } from '../../../hooks/useTimer';
import { resetPassword, sendPasswordResetToken } from '../resetPassword';
import useIonicToast from '../../../hooks/useIonicToast';
import { useIonViewDidLeave } from '@ionic/react';

export default function usePasswordResetService() {
  const [canChangePass, setCanChangePass] = useState<boolean>(false);
  const { counterTime, startCountDown, stopCountDown, isCounting } =
    useCountDown(5);
  const { presentIonToast } = useIonicToast();
  const aborters = useRef<{
    emailReq: AbortController | null;
    passwordReset: AbortController | null;
  }>({
    emailReq: null,
    passwordReset: null,
  });

  const requestPasswordResetPermit = async (identifier: string) => {
    try {
      aborters.current.emailReq = new AbortController();
      const trimmedIdentifier = identifier.trim().toLowerCase();
      await sendPasswordResetToken(
        {
          identifier: trimmedIdentifier,
          mode: 'email',
          via: 'email',
        },
        aborters.current?.emailReq.signal
      );
      if (!canChangePass) setCanChangePass(true);
      if (isCounting) stopCountDown();
      startCountDown();

      presentIonToast(
        `A password reset token has been sent to ${trimmedIdentifier}`
      );
    } catch (error: any) {
      if (error.name === 'AbortError') return;
      console.error(error.message);
      presentIonToast(error.message, 'error');
    }
  };

  const submitNewPassword = async (formData: {
    email: string;
    token: string;
    password: string;
    confirm_password: string;
  }) => {
    if (!canChangePass)
      throw new Error('you need to request for a reset token first');
    try {
      aborters.current.passwordReset = new AbortController();
      const { password: _, ...res } = formData;
      await resetPassword(
        {
          ...res,
          email: res.email.trim().toLowerCase(),
        },
        aborters.current?.passwordReset.signal
      );
      stopCountDown();
    } catch (error: any) {
      if (error.name === 'AbortError') return;
      presentIonToast(error.message, 'error');
    }
  };

  const cancelRequest = () => {
    if (aborters.current?.passwordReset) {
      aborters.current.passwordReset.abort();
      aborters.current.passwordReset = null;
    }
    if (aborters.current?.emailReq) {
      aborters.current.emailReq.abort();
      aborters.current.emailReq = null;
    }
    if (isCounting) stopCountDown();
  };

  useIonViewDidLeave(() => {
    cancelRequest();
  });

  return {
    requestPasswordResetPermit,
    submitNewPassword,
    cancelRequest,
    canChangePass,
    tokenTimer: counterTime,
    tokenIsValid: isCounting,
  };
}
